import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Calculator as CalcIcon,
  CheckCircle2,
  Coins,
  Copy,
  Factory,
  FlaskConical,
  History,
  Layers,
  Loader2,
  Package,
  Palette,
  Pencil,
  Plus,
  PowerOff,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dataBR, gramas, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ErrorState, Field, Loading, Surface, Thumb } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { DEV_STATUS, devStatusCls, resultCls } from "@/components/ds/devStatus";
import { ProjectModal } from "@/pages/Development";
import { PlateModal } from "@/pages/Plates";
import { DevFilamentModal } from "@/components/dev/DevFilamentModal";
import { DevPlateModal } from "@/components/dev/DevPlateModal";
import { DevTestModal } from "@/components/dev/DevTestModal";
import { DevCostModal } from "@/components/dev/DevCostModal";
import { DevConvertModal } from "@/components/dev/DevConvertModal";
import { DevTestPhotos } from "@/components/dev/DevTestPhotos";
import { DevCompareModal } from "@/components/dev/DevCompareModal";
import { DevSendProductionModal } from "@/components/dev/DevSendProductionModal";
import { DevEvaluateModal } from "@/components/dev/DevEvaluateModal";
import { SETTING_FIELDS } from "@/components/dev/DevTestModal";

const SETTING_LABELS = {
  ...Object.fromEntries(SETTING_FIELDS.map(([k, label]) => [k, label.split(" (")[0]])),
  outras: "Outras",
};

const TEST_STATUS = {
  PLANEJADO: "Planejado",
  ENVIADO: "Enviado para produção",
  EM_PRODUCAO: "Em produção",
  AGUARDANDO_AVALIACAO: "Aguardando avaliação",
  APROVADO: "Aprovado",
  AJUSTES: "Precisa de ajustes",
  FALHOU: "Falhou",
};

const PROD_STATUS = {
  PENDENTE: "Pendente",
  EM_PRODUCAO: "Em produção",
  CONCLUIDA: "Concluída",
  FALHOU: "Falhou",
  CANCELADA: "Cancelada",
};

const formatTime = (min) => {
  const t = Math.round(Number(min) || 0);
  if (!t) return "—";
  const h = Math.floor(t / 60);
  const m = t % 60;
  if (!h) return `${m}min`;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};

export default function DevelopmentProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);
  const [evaluating, setEvaluating] = useState(null);
  const [editingPlateId, setEditingPlateId] = useState(null);
  const [editingPlate, setEditingPlate] = useState(null);
  const [plateCtx, setPlateCtx] = useState({ filaments: [], products: [] });

  useEffect(() => {
    if (!editingPlateId) {
      setEditingPlate(null);
      return;
    }
    // A Plate vem da listagem oficial para trazer os filamentos com nome/foto já resolvidos.
    Promise.all([api.get("/filaments"), api.get("/products"), api.get("/plates")])
      .then(([f, p, pl]) => {
        setPlateCtx({ filaments: f.data, products: p.data });
        setEditingPlate(pl.data.find((x) => x.id === editingPlateId) || null);
      })
      .catch((e) => {
        toast.error(apiError(e));
        setEditingPlateId(null);
      });
  }, [editingPlateId]);

  const deleteCost = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/dev-projects/${id}/cost`);
      setProject(data);
      toast.success("Cálculo excluído. Projeto, Plates e testes continuam intactos.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const duplicateTest = async (t) => {
    try {
      const { data } = await api.post(`/dev-projects/${id}/tests/${t.id}/duplicate`);
      setProject(data);
      toast.success(`Teste criado a partir do #${t.attempt}.`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = useCallback(async () => {
    setError("");
    try {
      const [p, c] = await Promise.all([api.get(`/dev-projects/${id}`), api.get("/categories")]);
      setProject(p.data);
      setCats(c.data);
    } catch (e) {
      setError(apiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dev-projects/${id}/status`, { status });
      setProject(data);
      toast.success("Status atualizado.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const unlink = async (kind, itemId) => {
    try {
      const { data } = await api.delete(`/dev-projects/${id}/${kind}/${itemId}`);
      setProject(data);
      toast.success("Item desvinculado. O histórico dos testes continua salvo.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!project) return <Loading label="Abrindo projeto..." />;

  const approvedTest = (project.tests || []).find((t) => t.result === "APROVADO");
  const isClosed = project.status === "DESATIVADO";

  return (
    <div className="space-y-6" data-testid="project-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/desenvolvimento")}
            data-testid="back-to-projects-btn"
            className="mt-1 gap-1.5 text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Projetos
          </Button>
          <div className="min-w-0">
            <h1 className="truncate text-3xl font-semibold sm:text-4xl" data-testid="project-title">
              {project.name}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {project.code} · {project.category || "Sem categoria"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full border px-3 py-1.5 text-xs ${devStatusCls(project.status)}`}
            data-testid="project-status-badge"
          >
            {DEV_STATUS[project.status] || project.status}
          </span>
          {!isClosed && project.status !== "TRANSFORMADO" && project.status !== "APROVADO" && (
            <Button
              onClick={() => setConfirm("APROVAR")}
              data-testid="approve-project-btn"
              className="h-10 gap-2 rounded-full bg-emerald-500 font-semibold text-emerald-950 hover:bg-emerald-400"
            >
              <CheckCircle2 className="h-4 w-4" /> Aprovar projeto
            </Button>
          )}
          {project.status === "APROVADO" && (
            <Button
              onClick={() => setModal("convert")}
              data-testid="convert-project-btn"
              className="h-10 gap-2 rounded-full font-semibold"
            >
              <Package className="h-4 w-4" /> Transformar em produto
            </Button>
          )}
          {project.status === "TRANSFORMADO" && (
            <Button
              variant="outline"
              onClick={() => navigate("/produtos")}
              data-testid="go-to-product-btn"
              className="h-10 gap-2 rounded-full"
            >
              <Package className="h-4 w-4" /> Ver produto
            </Button>
          )}
          {isClosed ? (
            <Button
              variant="outline"
              onClick={() => setStatus("EM_DESENVOLVIMENTO")}
              data-testid="reactivate-project-btn"
              className="h-10 gap-2 rounded-full"
            >
              <RotateCcw className="h-4 w-4" /> Reativar
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={() => setConfirm("DESATIVAR")}
              data-testid="deactivate-project-btn"
              className="h-10 gap-2 rounded-full text-muted-foreground hover:text-destructive"
            >
              <PowerOff className="h-4 w-4" /> Desativar
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Surface className="space-y-4 p-5 lg:col-span-1" data-testid="project-identity-card">
          <div className="flex items-center justify-between gap-2">
            <p className="font-display text-base font-medium">Identificação</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setModal("edit")}
              data-testid="edit-project-btn"
              className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <Pencil className="h-3.5 w-3.5" /> Editar
            </Button>
          </div>
          <Thumb src={project.photo_path} alt={project.name} className="h-40 w-full rounded-xl" />
          {project.description && <p className="text-sm text-muted-foreground">{project.description}</p>}
          {project.notes && (
            <p className="rounded-lg border border-white/10 bg-secondary/40 p-3 text-xs text-muted-foreground">
              {project.notes}
            </p>
          )}
          <div className="space-y-1 border-t border-white/10 pt-3 text-xs text-muted-foreground">
            <p>Criado em {dataBR(project.created_at)}</p>
            <p>Atualizado em {dataBR(project.updated_at)}</p>
            {project.product_name && <p>Produto gerado: {project.product_name}</p>}
          </div>
        </Surface>

        <div className="space-y-4 lg:col-span-2">
          <Surface className="p-5" data-testid="project-materials-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-brand" />
                <p className="font-display text-base font-medium">Materiais</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setModal("filament")}
                data-testid="add-filament-btn"
                className="h-9 gap-1.5 rounded-full text-xs"
              >
                <Plus className="h-3.5 w-3.5" /> Adicionar filamento
              </Button>
            </div>
            {(project.filaments || []).length === 0 ? (
              <p className="mt-3 text-xs text-muted-foreground">
                Nenhum filamento vinculado. Use o botão acima para escolher um existente ou cadastrar
                um novo.
              </p>
            ) : (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {project.filaments.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5"
                    data-testid={`project-filament-${f.id}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm">
                        {f.material} {f.color_name}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {f.brand} · {gramas(f.balance_g)} em estoque
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => unlink("filaments", f.id)}
                      data-testid={`unlink-filament-btn-${f.id}`}
                      className="h-7 shrink-0 px-2 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Surface>

          <Surface className="p-5" data-testid="project-plates-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-brand" />
                <p className="font-display text-base font-medium">Plates</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setModal("plate")}
                  data-testid="create-plate-btn"
                  className="h-9 gap-1.5 rounded-full text-xs"
                >
                  <Plus className="h-3.5 w-3.5" /> Criar / vincular Plate
                </Button>
                {(project.plates || []).length > 0 && (
                  <Button
                    size="sm"
                    onClick={() => setSending("project")}
                    data-testid="send-project-production-btn"
                    className="h-9 gap-1.5 rounded-full text-xs font-semibold"
                  >
                    <Factory className="h-3.5 w-3.5" /> Enviar para produção
                  </Button>
                )}
              </div>
            </div>
            {(project.plates || []).length === 0 ? (
              <p className="mt-3 text-xs text-muted-foreground">
                Nenhuma Plate vinculada. Crie uma nova ou vincule uma que já existe.
              </p>
            ) : (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {project.plates.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5"
                    data-testid={`project-plate-${p.id}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm">{p.name}</p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {p.output_quantity} un · {formatTime(p.print_time_min)} · {gramas(p.total_g)}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingPlateId(p.id)}
                        data-testid={`edit-plate-btn-${p.id}`}
                        className="h-7 px-2 text-muted-foreground hover:text-foreground"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unlink("plates", p.id)}
                        data-testid={`unlink-plate-btn-${p.id}`}
                        className="h-7 px-2 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Surface>

          <Surface className="p-5" data-testid="project-cost-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <CalcIcon className="h-4 w-4 text-brand" />
                <p className="font-display text-base font-medium">Custos</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setModal("cost")}
                data-testid="calculate-cost-btn"
                className="h-9 gap-1.5 rounded-full text-xs"
              >
                <CalcIcon className="h-3.5 w-3.5" /> Calcular custo
              </Button>
            </div>
            {project.cost ? (
              <div className="mt-3 space-y-3" data-testid="project-cost-summary">
                {project.cost.stale && (
                  <p
                    className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
                    data-testid="project-cost-stale"
                  >
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    Cálculo desatualizado — {project.cost.stale_reason} Refaça o cálculo para ver o
                    custo atual.
                  </p>
                )}
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Último cálculo · {dataBR(project.cost.calculated_at)}
                </p>
                <div className="grid gap-3 sm:grid-cols-3">
                  {[
                    ["Custo por unidade", brl(project.cost.unit), "text-brand", "project-unit-cost"],
                    [`Custo total (${project.cost.quantity} un)`, brl(project.cost.total), "", "project-total-cost"],
                    ["Plate usada", project.cost.plate_name, "", "project-cost-plate"],
                  ].map(([label, value, cls, testid]) => (
                    <div key={label} className="rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5">
                      <p className="text-[11px] text-muted-foreground">{label}</p>
                      <p className={`truncate font-num text-lg ${cls}`} data-testid={testid}>
                        {value}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                  {[
                    ["Quantidade", `${project.cost.quantity} un`],
                    ["Perda estimada", `${project.cost.waste_percent ?? 0}%`],
                    ["Impressora", project.cost.printer_name || "Padrão"],
                    [
                      "Execuções",
                      project.cost.runs
                        ? `${project.cost.runs} · ${formatTime(project.cost.time_min)}`
                        : "—",
                    ],
                  ].map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <p className="text-muted-foreground">{label}</p>
                      <p className="truncate">{value}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 border-t border-white/10 pt-3">
                  <Button
                    size="sm"
                    onClick={() => setModal("cost")}
                    data-testid="redo-cost-btn"
                    className="h-9 gap-1.5 rounded-full text-xs font-semibold"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Refazer cálculo
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirm("EXCLUIR_CUSTO")}
                    data-testid="delete-cost-btn"
                    className="h-9 gap-1.5 rounded-full text-xs text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Excluir cálculo
                  </Button>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                Nenhum cálculo ainda. O custo usa a Calculadora do sistema com a Plate do projeto.
              </p>
            )}
          </Surface>
        </div>
      </div>

      <Surface className="p-5" data-testid="project-tests-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-brand" />
            <p className="font-display text-base font-medium">
              Testes <span className="font-num text-muted-foreground">({project.tests_count})</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={(project.tests || []).length < 2}
              onClick={() => setModal("compare")}
              data-testid="compare-tests-btn"
              className="h-10 gap-2 rounded-full px-4 text-xs"
            >
              <BarChart3 className="h-4 w-4" /> Comparar testes
            </Button>
            <Button
              onClick={() => setModal("test")}
              data-testid="new-test-btn"
              className="h-10 gap-2 rounded-full px-5 text-xs font-semibold"
            >
              <Plus className="h-4 w-4" /> Novo teste
            </Button>
          </div>
        </div>
        {(project.tests || []).length === 0 ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Nenhuma tentativa registrada. O teste é o planejamento: Plates, filamento e
            configurações. O material só é consumido quando você enviar para a Produção.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {project.tests.map((t) => (
              <div
                key={t.id}
                className="rounded-xl border border-white/10 bg-secondary/30 p-4"
                data-testid={`test-card-${t.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display text-sm">
                    Teste #{t.attempt}
                    {t.objective ? ` · ${t.objective}` : ""}
                  </p>
                  <span
                    className={`rounded-full border px-2.5 py-1 text-[11px] ${resultCls(t.result || t.status)}`}
                    data-testid={`test-result-${t.id}`}
                  >
                    {DEV_STATUS.RESULTS[t.result] || TEST_STATUS[t.status] || t.status}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                  <div>
                    <p className="text-muted-foreground">Quantidade</p>
                    <p className="font-num text-sm">{t.quantity || t.quantity_produced || 0} un</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tempo</p>
                    <p className="text-sm">{formatTime(t.real_time_min || t.print_time_min)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Plates</p>
                    <p className="truncate text-sm">
                      {(t.plate_names || []).join(", ") || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Produções</p>
                    <p className="font-num text-sm">{(t.productions || []).length}</p>
                  </div>
                </div>

                {t.objective && (
                  <p className="mt-3 text-xs text-muted-foreground">Objetivo: {t.objective}</p>
                )}
                {(t.filaments || []).length > 0 && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    {(t.filaments || [])
                      .map((i) => `${i.filament_name}${i.color_note ? ` (${i.color_note})` : ""}`)
                      .join(" · ")}
                  </p>
                )}
                {Object.entries(t.settings || {}).filter(([, v]) => v).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`test-settings-${t.id}`}>
                    {Object.entries(t.settings)
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <span
                          key={k}
                          className="rounded-full border border-white/10 bg-background/40 px-2 py-0.5 text-[11px] text-muted-foreground"
                        >
                          {SETTING_LABELS[k] || k}: {v}
                        </span>
                      ))}
                  </div>
                )}
                {(t.productions || []).length > 0 && (
                  <div className="mt-3 space-y-1.5 rounded-lg border border-white/10 bg-background/40 p-3">
                    {t.productions.map((pr) => (
                      <div
                        key={pr.id}
                        className="flex items-center justify-between gap-2 text-[11px]"
                        data-testid={`test-production-${pr.id}`}
                      >
                        <span className="min-w-0 truncate">{pr.plate_name}</span>
                        <span className="shrink-0 text-muted-foreground">
                          {PROD_STATUS[pr.status] || pr.status}
                          {pr.status === "CONCLUIDA" ? ` · ${pr.good_quantity} un boas` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {t.notes && (
                  <p className="mt-2 rounded-lg border border-white/10 bg-background/40 p-3 text-xs text-muted-foreground">
                    {t.notes}
                  </p>
                )}
                {t.result_notes && (
                  <p className="mt-2 rounded-lg border border-brand/25 bg-brand/10 p-3 text-xs">
                    Resultado: {t.result_notes}
                  </p>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {!t.result && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSending(t)}
                        data-testid={`send-production-btn-${t.id}`}
                        className="h-9 gap-1.5 rounded-full text-xs"
                      >
                        <Factory className="h-3.5 w-3.5" /> Enviar para produção
                      </Button>
                      {(t.productions || []).length > 0 && (
                        <Button
                          size="sm"
                          onClick={() => setEvaluating(t)}
                          data-testid={`evaluate-test-btn-${t.id}`}
                          className="h-9 gap-1.5 rounded-full text-xs font-semibold"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> Informar resultado
                        </Button>
                      )}
                    </>
                  )}
                  {(t.result === "FALHOU" || t.result === "AJUSTES") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => duplicateTest(t)}
                      data-testid={`duplicate-test-btn-${t.id}`}
                      className="h-9 gap-1.5 rounded-full text-xs"
                    >
                      <Copy className="h-3.5 w-3.5" /> Novo teste a partir deste
                    </Button>
                  )}
                </div>

                <DevTestPhotos projectId={project.id} test={t} onSaved={setProject} />
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {dataBR(t.created_at)} · {t.created_by}
                </p>
              </div>
            ))}
          </div>
        )}
      </Surface>

      <Surface className="p-5" data-testid="project-dev-cost-card">
        <div className="flex items-center gap-2">
          <Coins className="h-4 w-4 text-amber-300" />
          <p className="font-display text-base font-medium">Custo do desenvolvimento</p>
        </div>
        {project.dev_cost?.tests ? (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
              {[
                ["Testes realizados", `${project.dev_cost.tests}`, ""],
                ["Material consumido", gramas(project.dev_cost.used_g), ""],
                ["Material perdido", gramas(project.dev_cost.waste_g), "text-red-300"],
                ["Custo dos materiais", brl(project.dev_cost.used_cost), ""],
                ["Custo das perdas", brl(project.dev_cost.waste_cost), "text-red-300"],
                ["Custo total", brl(project.dev_cost.total_cost), "text-brand"],
              ].map(([label, value, cls]) => (
                <div key={label} className="rounded-lg border border-white/10 bg-secondary/40 px-3 py-2.5">
                  <p className="text-[11px] text-muted-foreground">{label}</p>
                  <p className={`font-num text-lg ${cls}`} data-testid={`dev-cost-${label}`}>
                    {value}
                  </p>
                </div>
              ))}
            </div>
            {(project.dev_cost.by_filament || []).length > 0 && (
              <div className="mt-4 space-y-2 border-t border-white/10 pt-3">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Por filamento
                </p>
                {project.dev_cost.by_filament.map((f) => (
                  <div
                    key={f.filament_id}
                    className="flex flex-wrap items-center justify-between gap-2 text-xs"
                    data-testid={`dev-cost-filament-${f.filament_id}`}
                  >
                    <span className="min-w-0 truncate">{f.name}</span>
                    <span className="shrink-0 font-num text-muted-foreground">
                      {gramas(f.grams)} usados · {gramas(f.waste_g)} perdidos · {brl(f.cost)}
                    </span>
                  </div>
                ))}
                {project.dev_cost.by_filament.some((f) => !f.cost_per_g) && (
                  <p className="text-[11px] text-amber-300">
                    Algum filamento não tem valor de compra registrado, então o custo dele fica em
                    R$ 0,00.
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground">
            Nenhum teste registrado ainda. O custo aparece aqui conforme as produções de
            desenvolvimento são concluídas, usando o valor real por grama de cada filamento.
          </p>
        )}
      </Surface>

      <Surface className="p-5" data-testid="project-history-card">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-brand" />
          <p className="font-display text-base font-medium">Histórico</p>
        </div>
        <ul className="mt-3 space-y-2">
          {[...(project.history || [])].reverse().map((h, i) => (
            <li key={i} className="flex items-start justify-between gap-3 text-xs">
              <span className="text-muted-foreground">{h.note || h.status}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{dataBR(h.at)}</span>
            </li>
          ))}
        </ul>
      </Surface>

      {modal === "edit" && (
        <ProjectModal
          project={project}
          cats={cats}
          onClose={() => setModal(null)}
          onSaved={() => load()}
        />
      )}
      {modal === "filament" && (
        <DevFilamentModal
          projectId={project.id}
          linked={(project.filaments || []).map((f) => f.id)}
          onClose={() => setModal(null)}
          onSaved={setProject}
        />
      )}
      {modal === "plate" && (
        <DevPlateModal
          projectId={project.id}
          projectName={project.name}
          linked={(project.plates || []).map((p) => p.id)}
          onClose={() => setModal(null)}
          onSaved={setProject}
        />
      )}
      {modal === "test" && (
        <DevTestModal
          project={project}
          onClose={() => setModal(null)}
          onSaved={setProject}
        />
      )}
      {sending && (
        <DevSendProductionModal
          project={project}
          test={sending === "project" ? null : sending}
          onClose={() => setSending(null)}
          onSaved={setProject}
        />
      )}
      {evaluating && (
        <DevEvaluateModal
          projectId={project.id}
          test={evaluating}
          onClose={() => setEvaluating(null)}
          onSaved={setProject}
        />
      )}
      {modal === "compare" && (
        <DevCompareModal tests={project.tests || []} onClose={() => setModal(null)} />
      )}
      {editingPlate && (
        <PlateModal
          plate={editingPlate}
          filaments={plateCtx.filaments}
          products={plateCtx.products}
          requireProduct={false}
          onClose={() => setEditingPlateId(null)}
          onSaved={load}
        />
      )}
      {modal === "cost" && (
        <DevCostModal project={project} onClose={() => setModal(null)} onSaved={load} />
      )}
      {modal === "convert" && (
        <DevConvertModal
          project={project}
          cats={cats}
          onClose={() => setModal(null)}
          onSaved={(res) => {
            setProject(res.project);
            toast.success(
              res.stock_transfer
                ? `Produto ${res.product.name} criado com ${res.stock_transfer.quantity} un em estoque.`
                : `Produto ${res.product.name} criado.`
            );
          }}
        />
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="project-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "APROVAR"
                ? "Aprovar projeto"
                : confirm === "EXCLUIR_CUSTO"
                ? "Excluir o cálculo salvo?"
                : "Desativar projeto"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {confirm === "EXCLUIR_CUSTO" ? (
                  <p>
                    Apaga somente o resultado do cálculo de custo. O projeto, as Plates, os
                    filamentos, os testes, as produções, o histórico e o estoque continuam
                    exatamente como estão.
                  </p>
                ) : confirm === "APROVAR" ? (
                  <>
                    <p>Resumo do que foi desenvolvido:</p>
                    <ul className="list-disc space-y-1 pl-4 text-xs">
                      <li>
                        Materiais:{" "}
                        {(project.filaments || [])
                          .map((f) => `${f.material} ${f.color_name}`)
                          .join(", ") || "nenhum"}
                      </li>
                      <li>
                        Plates: {(project.plates || []).map((p) => p.name).join(", ") || "nenhuma"}
                      </li>
                      <li>Custo: {project.cost ? `${brl(project.cost.unit)} por unidade` : "não calculado"}</li>
                      <li>
                        Último teste aprovado:{" "}
                        {approvedTest ? `#${approvedTest.attempt} em ${dataBR(approvedTest.created_at)}` : "nenhum"}
                      </li>
                      <li>Observações: {project.notes || "—"}</li>
                    </ul>
                    <p className="text-xs">
                      O projeto continua existindo com todo o histórico. Depois de aprovar você poderá
                      transformá-lo em produto.
                    </p>
                  </>
                ) : (
                  <p>
                    Nada é excluído: o projeto sai da lista principal e continua acessível pelo filtro
                    "Desativados", com todos os testes, consumos e perdas salvos.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-project-confirm-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                if (confirm === "EXCLUIR_CUSTO") {
                  deleteCost();
                  return;
                }
                setStatus(confirm === "APROVAR" ? "APROVADO" : "DESATIVADO");
              }}
              data-testid="confirm-project-btn"
            >
              {busy
                ? "Salvando..."
                : confirm === "APROVAR"
                ? "Aprovar projeto"
                : confirm === "EXCLUIR_CUSTO"
                ? "Excluir cálculo"
                : "Desativar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
